var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Card, CardContent, CardHeader, Grid, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveUserIntraSquadTournamentPermissions } from "../api/PermissionApiProvider";
import { useDeleteIntraSquadTournament, useListIntraSquadTournamentMatches, useListIntraSquadTournamentTeams, useRetrieveIntraSquadTournament, } from "../api/TournamentApiProvider";
import { useRetrievePlayersByTeamId, useRetrieveTeam } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { SquadCard } from "../teams/SquadCard";
import { TeamColorBox } from "../teams/TeamColorBox";
import { DeleteDialog } from "../utils/DeleteDialog";
import { useNumericParams } from "../utils/useNumericParams";
import { LeagueTable } from "./IntraSquadTournamentLeagueTable";
import { IntraSquadTournamentMatchCard } from "./IntraSquadTournamentMatchCard";
var splitOnCapitals = function (s) { return s.replace(/([a-z])([A-Z])/g, "$1 $2"); };
export var IntraSquadTournament = function () {
    var id = useNumericParams().id;
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    var _a = __read(React.useState(false), 2), showDelete = _a[0], setShowDelete = _a[1];
    var navigate = useNavigate();
    var _b = useRetrieveUserIntraSquadTournamentPermissions({ tournamentId: id }), userTournamentPermissions = _b.data, userTournamentPermissionsIsLoading = _b.isLoading, userTournamentPermissionsError = _b.error;
    var _c = userTournamentPermissions || {}, _d = _c.canModify, canModify = _d === void 0 ? false : _d, _e = _c.canView, canView = _e === void 0 ? false : _e;
    var _f = useRetrieveIntraSquadTournament({ id: id, enabled: canView }), tournament = _f.data, isLoading = _f.isLoading, error = _f.error;
    var _g = useRetrieveTeam({ id: tournament === null || tournament === void 0 ? void 0 : tournament.team, enabled: canView }), team = _g.data, isTeamLoading = _g.isLoading, teamError = _g.error;
    var _h = useListIntraSquadTournamentMatches({
        tournamentId: id,
        enabled: canView,
    }), matches = _h.data, isMatchesLoading = _h.isLoading, matchesError = _h.error;
    var _j = useListIntraSquadTournamentTeams({ tournamentId: id, enabled: canView }), tournamentTeams = _j.data, isTournamentTeamsLoading = _j.isLoading, tournamentTeamsError = _j.error;
    var mutation = useDeleteIntraSquadTournament({
        intraSquadTournament: tournament,
        team: team,
        onMutate: function () { },
        onSuccess: function () {
            return navigate("/teams/".concat(tournament === null || tournament === void 0 ? void 0 : tournament.team));
        },
        onError: console.error,
    });
    var _k = useRetrievePlayersByTeamId({
        teamId: team === null || team === void 0 ? void 0 : team.id,
        enabled: canView && !!(team === null || team === void 0 ? void 0 : team.id),
    }), players = _k.data, isPlayersLoading = _k.isLoading, playersError = _k.error;
    var composedMap = useMemo(function () {
        if (!players || !tournamentTeams)
            return null;
        return {
            players: new Map(players.map(function (p) { return [p.id, p]; })),
            tournamentTeams: new Map(tournamentTeams.map(function (t) { return [t.id, t]; })),
        };
    }, [players, tournamentTeams]);
    var _l = composedMap || {}, playersMap = _l.players, tournamentTeamsMap = _l.tournamentTeams;
    var handleCloseDeleteDialog = function () {
        setShowDelete(false);
    };
    var handleDelete = function () {
        mutation.mutate();
    };
    var matchesByRound = useMemo(function () {
        return (matches || []).reduce(function (acc, match) {
            acc[match.round] = acc[match.round] || [];
            acc[match.round].push(match);
            return acc;
        }, {});
    }, [matches]);
    var loading = userTournamentPermissionsIsLoading ||
        (canView &&
            (isLoading ||
                isTeamLoading ||
                isMatchesLoading ||
                isTournamentTeamsLoading ||
                isPlayersLoading));
    var _m = __read(useState(null), 2), anchorEl = _m[0], setAnchorEl = _m[1];
    var open = Boolean(anchorEl);
    var handleMenuOpen = function (event) {
        setAnchorEl(event.currentTarget);
    };
    var handleMenuClose = function () {
        setAnchorEl(null);
    };
    if (userTournamentPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userTournamentPermissionsError) {
        return _jsx(ErrorView, { error: userTournamentPermissionsError });
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (error ||
        teamError ||
        matchesError ||
        tournamentTeamsError ||
        playersError) {
        return (_jsx(ErrorView, { error: error ||
                teamError ||
                matchesError ||
                tournamentTeamsError ||
                new Error("Failed to retrieve tournament data.") }));
    }
    if (loading) {
        return _jsx(Loading, {});
    }
    if (!players ||
        !tournament ||
        !team ||
        !matches ||
        !tournamentTeams ||
        !tournamentTeamsMap ||
        !playersMap) {
        console.log(players, tournament, team, matches, tournamentTeams, tournamentTeamsMap, playersMap);
        return (_jsx(ErrorView, { error: new Error("Incomplete data for tournament display.") }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(DeleteDialog, { open: showDelete, title: "Delete Intra-Squad Tournament?", onCancel: handleCloseDeleteDialog, onConfirm: handleDelete }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Typography, { variant: "subtitle1", component: "div", align: "center", children: [_jsx("span", { style: { color: "#6c757d" }, children: format(new Date(tournament.startTime), "MMMM do yyyy @ H:mma") }), " ", "\u2022", " ", _jsx(Link, { style: { textDecoration: "none" }, to: "/teams/".concat(team.id), children: team.name })] }), action: canModify &&
                            (isMobile ? (_jsxs(Box, { sx: {
                                    minWidth: 0,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                }, children: [_jsx(IconButton, { onClick: handleMenuOpen, children: _jsx(MoreVertIcon, {}) }), _jsx(Menu, { anchorEl: anchorEl, open: open, onClose: handleMenuClose, anchorOrigin: { vertical: "bottom", horizontal: "right" }, transformOrigin: { vertical: "top", horizontal: "right" }, keepMounted: true, children: _jsx(MenuItem, { onClick: function () {
                                                handleMenuClose();
                                                setShowDelete(true);
                                            }, children: _jsx(DeleteIcon, {}) }) })] })) : (_jsx(Stack, { direction: "row", spacing: 1, children: _jsx(IconButton, { onClick: function () { return setShowDelete(true); }, children: _jsx(DeleteIcon, {}) }) }))) }), _jsx(CardContent, { children: _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "baseline", flexWrap: "nowrap", sx: { gap: 1, whiteSpace: "nowrap" }, children: _jsx(Typography, { variant: "h5", children: tournament.tournamentName }) }) })] }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { item: true, xs: 12, md: 9, children: [_jsx(LeagueTable, { matches: matches }), _jsx(Grid, { container: true, spacing: 4, children: Object.entries(matchesByRound).map(function (_a) {
                                    var _b = __read(_a, 2), round = _b[0], roundMatches = _b[1];
                                    return (_jsxs(Grid, { item: true, xs: 12, children: [_jsxs(Typography, { variant: "h6", gutterBottom: true, children: ["Round ", round] }), _jsx(Grid, { container: true, spacing: 2, children: roundMatches.map(function (match) { return (_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(IntraSquadTournamentMatchCard, { match: match, canModify: canModify }) }, match.id)); }) })] }, round));
                                }) })] }), _jsxs(Grid, { item: true, xs: 12, md: 3, container: true, spacing: 2, direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "Details" }), _jsx(Stack, { spacing: 1, children: [
                                                    ["Type", splitOnCapitals(tournament.tournamentType)],
                                                    ["Half Length", "".concat(tournament.halfLength, " mins")],
                                                    ["Half Time", "".concat(tournament.halfTimeLength, " mins")],
                                                    ["Break Between Matches", "".concat(tournament.breakLength, " mins")],
                                                ].map(function (_a) {
                                                    var _b = __read(_a, 2), label = _b[0], value = _b[1];
                                                    return (_jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), _jsx(Typography, { variant: "body2", children: value })] }, label));
                                                }) })] }) }) }), _jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h6", gutterBottom: true, children: "Teams" }) }), tournamentTeams.map(function (tournamentTeam, i) { return (_jsx(Grid, { item: true, children: _jsx(SquadCard, { title: _jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsx(TeamColorBox, { color: tournamentTeam.primaryColour, onClick: function () { } }), _jsx(Typography, { component: "span", variant: "body2", children: tournamentTeam.name })] }), players: tournamentTeam.players
                                        .map(function (playerId) { return playersMap === null || playersMap === void 0 ? void 0 : playersMap.get(playerId); })
                                        .filter(function (p) { return p !== undefined; }) }) }, i)); })] })] })] }));
};
